/**
 * The Game Lists tab with the brand's OWN lists gated off (the `owner-game-lists` feature flag).
 *
 * Gated off, the tab behaves as it did before own lists existed: nothing is read, and the lists read as
 * loaded and empty rather than as still loading. The page's arrangement waits on its own lists before it can
 * be edited or published, so "never read" has to count as ready, or the arrangement would wait forever.
 */
import type { FeatureFlagKey } from '../../config/featureFlags';
import type { PageArrangement } from './page-arrangement-api';
import { withOwnLists, type PageOwnList } from './page-lists';

/** The flag the brand's own lists are behind (Game Lists' Add list among them; FTUX's list piece reads it too). */
export const OWN_LISTS_FLAG: FeatureFlagKey = 'owner-game-lists';

/** The one empty value handed out while gated off, so a memo keyed on it does not recompute every render. */
const NO_LISTS: Readonly<Record<string, never>> = Object.freeze({});

/**
 * Whether the brand's own lists are in (`ready`) and which they are (`lists`). Gated off: ready, and none,
 * whatever an earlier read stored. On: ready once they have been read, and the stored lists as they are.
 */
export const ownListsState = <T extends object>(
  enabled: boolean,
  loaded: boolean,
  stored: T,
): { ready: boolean; lists: T | Readonly<Record<string, never>> } =>
  enabled ? { ready: loaded, lists: stored } : { ready: true, lists: NO_LISTS };

/**
 * The page's arrangement brought in line with the brand's own lists (see `withOwnLists`), only while the
 * feature is on. Gated off it is left as it is: with no lists to go by, `withOwnLists` would drop every
 * section the brand already has on its page, which reads as a change and a publish would carry to the site.
 */
export const arrangementWithOwnLists = (
  enabled: boolean,
  arrangement: PageArrangement,
  own: PageOwnList[],
): PageArrangement => (enabled ? withOwnLists(arrangement, own) : arrangement);
